import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { COLORS } from '@repo/shared_utils'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, Clock, Users } from 'lucide-react'

export const Route = createFileRoute('/dashboard/hr_admin/')({
  component: RouteComponent,
})

function RouteComponent() {
  const managementOptions = [
    {
      title: 'Manage Employees',
      description: 'View, add, edit, and manage employee information and settings',
      path: '/dashboard/hr_admin/employees',
      icon: <Users className="h-8 w-8" />,
      color: COLORS[1], // indigo-500
    },
    {
      title: 'Manage Timesheets',
      description: 'Review, approve, and manage employee timesheets and attendance',
      path: '/dashboard/hr_admin/timesheets',
      icon: <Clock className="h-8 w-8" />,
      color: COLORS[2], // violet-600
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Background decorative elements */}
      <div
        className="absolute top-20right-20 w-64 h-64 rounded-full opacity-10 blur-3"
        style={{ backgroundColor: COLORS[1] }}
      />
      <div
        className="absolute bottom-20 left-20 w-64 h-64 rounded-full opacity-10 blur-3"
        style={{ backgroundColor: COLORS[2] }}
      />

      <div className="relative max-w-6xl mx-auto p-4">
        <DashboardShell>
          <DashboardHeader
            heading="HR Admin Dashboard"
            text="Manage your organization's employees and timesheets efficiently"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {managementOptions.map((option) => (
              <Card
                key={option.path}
                className="group relative overflow-hidden bg-white/80 dark:bg-gray-9000rop-blur-lg border-0ow-lg hover:shadow-xl transition-all duration-30 hover:scale-105"
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${option.color}15` }}
                    >
                      <div style={{ color: option.color }}>{option.icon}</div>
                    </div>
                    <ArrowRight className="h-5-5t-muted-foreground transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                  <CardTitle className="text-xl font-semibold" style={{ color: option.color }}>
                    {option.title}
                  </CardTitle>
                  <CardDescription className="text-base leading-relaxed">
                    {option.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className=" mt-auto">
                  <Link to={option.path} className="w-full">
                    <Button
                      size="lg"
                      className="w-full mt-auto h-12t-base font-medium rounded-xl transition-all duration-300nsform hover:scale-105ow-md hover:shadow-lg group/btn relative overflow-hidden"
                      style={{ backgroundColor: option.color }}
                    >
                      <div
                        className="absolute inset-0dient-to-r opacity-0group-hover/btn:opacity-20 transition-opacity duration-300"
                        style={{
                          backgroundImage: `linear-gradient(to right, ${option.color}, ${COLORS[4]})`,
                        }}
                      />
                      <span className="relative flex items-center gap-2">
                        Access {option.title}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300over/btn:translate-x-1" />
                      </span>
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick stats or info section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <div
              className="p-6ded-xl text-center transition-all duration-30 hover:scale-105"
              style={{ backgroundColor: `${COLORS[1]}8` }}
            >
              <div className="text-3xl font-bold" style={{ color: COLORS[1] }}>
                HR Admin
              </div>
              <div className="text-sm text-muted-foreground mt-1">Role</div>
            </div>
            <div
              className="p-6ded-xl text-center transition-all duration-30 hover:scale-105"
              style={{ backgroundColor: `${COLORS[2]}8` }}
            >
              <div className="text-3xl font-bold" style={{ color: COLORS[2] }}>
                247
              </div>
              <div className="text-sm text-muted-foreground mt-1">Access</div>
            </div>
            <div
              className="p-6ded-xl text-center transition-all duration-30 hover:scale-105"
              style={{ backgroundColor: `${COLORS[4]}8` }}
            >
              <div className="text-3xl font-bold" style={{ color: COLORS[4] }}>
                Secure
              </div>
              <div className="text-sm text-muted-foreground mt-1">Portal</div>
            </div>
          </div>
        </DashboardShell>
      </div>
    </div>
  )
}
